import { Access } from "@p0security/shared/types/access/index";

export const GithubRepositoriesAccesses = {
  role: Access({
    description: "A direct collaborator role on a GitHub repository",
    label: "Repository role",
    maturity: "beta",
    delegation: {},
    generated: {
      // The user's direct role on the repository when the request was staged,
      // in the form GitHub's collaborator PUT takes. Absent when they had
      // none. Revoking restores it, or removes the direct role if absent.
      priorRole: {
        type: "string",
        label: "Prior role",
        description: "The direct role restored when this access ends",
        optional: true,
      },
      // The grantee's GitHub user id: the numeric id behind their login. A
      // login can be renamed and then claimed by someone else, so the
      // connector acts on the account.
      githubUserId: { type: "string", label: "GitHub user ID" },
      // The repository's GitHub id. A repository can be renamed, so P0 tells
      // two requests are for one repository by its id. Absent on a request
      // staged before P0 recorded it, or when the connector couldn't look it
      // up; P0 matches those by name.
      repositoryId: {
        type: "string",
        label: "GitHub repository ID",
        optional: true,
      },
    },
    parameters: {
      // The full name, `<org>/<repo>`: bare repository names are only unique
      // within one organization. The description says `{org}/{repo}` because
      // Slack shows `<` and `>` in a placeholder as `&lt;` and `&gt;`.
      repository: {
        type: "dynamic",
        label: "Repository",
        description: "GitHub repository, as {org}/{repo}",
      },
      role: {
        type: "dynamic",
        label: "Role",
        description: "Repository role to grant",
      },
      username: {
        type: "generated",
        label: "GitHub username",
        description: "The requestor's login in the organization",
      },
    },
    render: { locator: "repository", policy: "role" } as const,
    // GitHub gives a person one role per repository
    singleValued: { parameter: "role", per: "repository" },
  }),
};

export type GithubRepositoriesAccesses = typeof GithubRepositoriesAccesses;
export type GithubRepositoriesRoleAccess = GithubRepositoriesAccesses["role"];
